import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, Search, Phone, Mail, Calendar, MapPin,
  CheckCircle2, Clock, RefreshCw, MessageSquare, StickyNote, Plus, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const http = axios.create({ baseURL: API, withCredentials: true });

async function authedGet(url) {
  try {
    return await http.get(url);
  } catch (e) {
    if (e.response?.status === 401) {
      await http.post("/api/auth/refresh");
      return await http.get(url);
    }
    throw e;
  }
}

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await http.post("/api/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-sky-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-card">
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_audicion-amplisoon/artifacts/vici73nm_AMPLISOON.png"
            alt="Amplisoon"
            className="h-14 w-auto rounded-lg mb-4"
          />
          <div className="inline-flex items-center gap-2 text-blue-950 font-bold text-xl">
            <Lock className="w-5 h-5" /> Panel de citas
          </div>
          <p className="text-sm text-slate-500 mt-1">Acceso privado para el equipo Amplisoon</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
              placeholder="admin@amplisoon.com"
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
              placeholder="••••••••"
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-blue-950 hover:bg-blue-900 text-white font-bold rounded-xl transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}

const CENTRE_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "campello", label: "El Campello" },
  { id: "torrellano", label: "Torrellano" },
];

function StatusBadge({ status }) {
  if (status === "contacted") {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
        <CheckCircle2 className="w-3.5 h-3.5" /> Contactada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
      <Clock className="w-3.5 h-3.5" /> Pendiente
    </span>
  );
}

function NotesSection({ appt, onAddNote, onDeleteNote }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = appt.notes || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onAddNote(appt, text.trim());
      setText("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-dashed border-slate-200" data-testid={`notes-section-${appt.id}`}>
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
        <StickyNote className="w-3.5 h-3.5 text-amber-500" /> Notas internas
      </div>
      {notes.length > 0 && (
        <ul className="space-y-1.5 mb-3">
          {notes.map((n) => (
            <li
              key={n.id}
              className="group flex items-start justify-between gap-2 text-sm bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5"
              data-testid={`note-item-${n.id}`}
            >
              <div>
                <span className="text-slate-800">{n.text}</span>
                <span className="ml-2 text-[11px] text-slate-400">
                  {new Date(n.created_at).toLocaleDateString("es-ES")}{" "}
                  {new Date(n.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <button
                onClick={() => onDeleteNote(appt, n)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity shrink-0 mt-0.5"
                title="Borrar nota"
                data-testid={`delete-note-${n.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={500}
          placeholder='Añadir nota (ej. "llamé, no contesta")...'
          className="flex-1 h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
          data-testid={`note-input-${appt.id}`}
        />
        <button
          type="submit"
          disabled={saving || !text.trim()}
          className="inline-flex items-center gap-1 px-3 h-9 rounded-lg bg-amber-400 hover:bg-amber-500 text-blue-950 text-sm font-bold transition-colors disabled:opacity-50"
          data-testid={`note-submit-${appt.id}`}
        >
          <Plus className="w-4 h-4" /> Añadir
        </button>
      </form>
    </div>
  );
}

function AppointmentCard({ appt, onToggle, onAddNote, onDeleteNote }) {
  const date = new Date(appt.created_at);
  const contacted = appt.status === "contacted";
  return (
    <div
      className={`bg-white rounded-2xl border p-5 shadow-sm transition-all ${contacted ? "border-emerald-200 opacity-80" : "border-slate-200"}`}
      data-testid={`appointment-card-${appt.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-bold text-lg text-blue-950" data-testid="appointment-name">{appt.name}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-600">
            <a href={`tel:${appt.phone}`} className="inline-flex items-center gap-1.5 hover:text-blue-800 font-medium">
              <Phone className="w-3.5 h-3.5" /> {appt.phone}
            </a>
            {appt.email && (
              <a href={`mailto:${appt.email}`} className="inline-flex items-center gap-1.5 hover:text-blue-800">
                <Mail className="w-3.5 h-3.5" /> {appt.email}
              </a>
            )}
          </div>
        </div>
        <StatusBadge status={appt.status} />
      </div>

      <div className="grid sm:grid-cols-3 gap-2 mt-4 text-sm">
        <div className="flex items-center gap-2 text-slate-700">
          <MapPin className="w-4 h-4 text-sky-600 shrink-0" />
          {appt.centre || "Sin centro"}
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <MessageSquare className="w-4 h-4 text-sky-600 shrink-0" />
          {appt.service}
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <Calendar className="w-4 h-4 text-sky-600 shrink-0" />
          {appt.preferred_date ? `Prefiere: ${appt.preferred_date}` : "Sin fecha preferida"}
        </div>
      </div>

      {appt.message && (
        <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
          {appt.message}
        </p>
      )}

      <NotesSection appt={appt} onAddNote={onAddNote} onDeleteNote={onDeleteNote} />

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-400">
          Recibida el {date.toLocaleDateString("es-ES")} a las {date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
        </span>
        <button
          onClick={() => onToggle(appt)}
          className={`px-4 h-9 rounded-lg text-sm font-bold transition-colors ${
            contacted
              ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
              : "bg-blue-950 text-white hover:bg-blue-900"
          }`}
          data-testid={`toggle-status-${appt.id}`}
        >
          {contacted ? "Marcar pendiente" : "Marcar contactada"}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [centreFilter, setCentreFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    authedGet("/api/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedGet("/api/appointments");
      setAppointments(data);
    } catch {
      toast.error("No se pudieron cargar las citas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) loadAppointments();
  }, [user, loadAppointments]);

  const toggleStatus = async (appt) => {
    const newStatus = appt.status === "contacted" ? "pending" : "contacted";
    try {
      await http.patch(`/api/appointments/${appt.id}`, { status: newStatus });
      setAppointments((prev) => prev.map((a) => (a.id === appt.id ? { ...a, status: newStatus } : a)));
      toast.success(newStatus === "contacted" ? "Cita marcada como contactada" : "Cita marcada como pendiente");
    } catch {
      toast.error("No se pudo actualizar la cita");
    }
  };

  const addNote = async (appt, text) => {
    try {
      const { data } = await http.post(`/api/appointments/${appt.id}/notes`, { text });
      setAppointments((prev) =>
        prev.map((a) => (a.id === appt.id ? { ...a, notes: [...(a.notes || []), data] } : a))
      );
      toast.success("Nota añadida");
    } catch {
      toast.error("No se pudo guardar la nota");
    }
  };

  const deleteNote = async (appt, note) => {
    try {
      await http.delete(`/api/appointments/${appt.id}/notes/${note.id}`);
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appt.id ? { ...a, notes: (a.notes || []).filter((n) => n.id !== note.id) } : a
        )
      );
      toast.success("Nota borrada");
    } catch {
      toast.error("No se pudo borrar la nota");
    }
  };

  const logout = async () => {
    try { await http.post("/api/auth/logout"); } catch {}
    setUser(false);
    setAppointments([]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return appointments.filter((a) => {
      if (centreFilter === "campello" && !(a.centre || "").toLowerCase().includes("campello")) return false;
      if (centreFilter === "torrellano" && !(a.centre || "").toLowerCase().includes("torrellano")) return false;
      if (q && !a.name.toLowerCase().includes(q) && !a.phone.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [appointments, centreFilter, search]);

  const pendingCount = appointments.filter((a) => a.status !== "contacted").length;

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">
        Cargando...
      </div>
    );
  }

  if (user === false) return <LoginView onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="bg-blue-950 text-white sticky top-0 z-40 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_audicion-amplisoon/artifacts/vici73nm_AMPLISOON.png"
              alt="Amplisoon"
              className="h-9 w-auto rounded"
            />
            <span className="font-bold hidden sm:inline">Panel de citas</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadAppointments}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualizar</span>
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white/10 hover:bg-red-500/80 text-sm font-semibold transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-blue-950">Citas recibidas</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="admin-stats">
              {appointments.length} en total · <span className="font-bold text-amber-600">{pendingCount} pendientes</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex rounded-xl bg-white border border-slate-200 p-1 shadow-sm" data-testid="centre-filter">
            {CENTRE_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setCentreFilter(f.id)}
                className={`px-4 h-10 rounded-lg text-sm font-bold transition-colors ${
                  centreFilter === f.id ? "bg-blue-950 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`centre-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o teléfono..."
              className="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-200 bg-white shadow-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {loading && appointments.length === 0 ? (
          <div className="text-center py-16 text-slate-500">Cargando citas...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300 text-slate-500" data-testid="admin-empty-state">
            No hay citas que coincidan con los filtros.
          </div>
        ) : (
          <div className="space-y-4" data-testid="appointments-list">
            {filtered.map((appt) => (
              <AppointmentCard
                key={appt.id}
                appt={appt}
                onToggle={toggleStatus}
                onAddNote={addNote}
                onDeleteNote={deleteNote}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
